// Inspired by https://github.com/WebDevSimplified/useful-custom-react-hooks/blob/main/src/8-useStorage/useStorage.js
import { useCallback, useState, useEffect } from 'react';
/**
 * A custom hook for managing state stored in localStorage or sessionStorage.
 *
 * @param key The key under which the state will be stored.
 * @param defaultValue The initial value for the state, or a function that returns the initial value.
 * @param storageType The type of storage to use: localStorage or sessionStorage.
 *
 * @returns A tuple containing the state value, a function to update the state, and a function
 *          to remove the value from storage.
 */
const useStorage = (key, defaultValue, storageType) => {
    // Determine the storage object based on the specified storage type
    const storageObject = storageType === 'localStorage' ? window.localStorage : window.sessionStorage;
    // Initialize state with the value from storage, or the default value
    const [value, setValue] = useState(() => {
        const jsonValue = storageObject.getItem(key);
        if (jsonValue) {
            return JSON.parse(jsonValue);
        }
        if (typeof defaultValue === 'function') {
            return defaultValue();
        }
        return defaultValue;
    });
    // Update storage whenever the value changes
    useEffect(() => {
        if (!value) {
            storageObject.removeItem(key);
        }
        else {
            storageObject.setItem(key, JSON.stringify(value));
        }
    }, [key, value, storageObject]);
    // Callback to remove the value from storage
    const remove = useCallback(() => {
        setValue(undefined);
    }, []);
    return [value, setValue, remove];
};
/**
 * A custom hook for managing state stored in localStorage.
 *
 * @param key The key under which the state will be stored in localStorage.
 * @param defaultValue The initial value for the state, or a function that returns the initial value.
 *
 * @returns A tuple containing the state value, a function to update the state, and a function
 *          to remove the value from localStorage.
 */
export const useLocalStorage = (key, defaultValue) => {
    return useStorage(key, defaultValue, 'localStorage');
};
/**
 * A custom hook for managing state stored in sessionStorage.
 *
 * @param key The key under which the state will be stored in sessionStorage.
 * @param defaultValue The initial value for the state, or a function that returns the initial value.
 *
 * @returns A tuple containing the state value, a function to update the state,
 *          and a function to remove the value from sessionStorage.
 */
export const useSessionStorage = (key, defaultValue) => {
    return useStorage(key, defaultValue, 'sessionStorage');
};
